import {
  FetchSearchProviderDataParams,
  FetchSearchProviderDataResponse,
  FetchSearchProviderFacetsParams,
  FetchSearchProviderFacetsResponse,
  FetchSearchProviderAutocompleteParams,
  FetchSearchProviderAutocompleteResponse,
  FetchSearchProviderRecommendationsParams,
  FetchSearchProviderRecommendationsResponse,
  SearchApi,
} from '../../types'
import { transformConstructorFacetsToFilters } from './transformConstructorFacetsToFilters'
import { transformConstructorSearchResults } from './transformConstructorSearchResults'
import { getSortValues, rulesToSendFilters } from '../../helpers'
import {
  constructorGetData,
  constructorGetFacets,
  constructorGetAutocomplete,
  constructorGetRecommendations,
} from '../../libs/constructor'
import { transformConstructorAutocompleteResults } from './transformConstructorAutocompleteResults'

type ConstructorSearchServiceOptions = {
  userId: string
}

export class ConstructorSearchService implements SearchApi {
  private userId: string

  constructor(params: ConstructorSearchServiceOptions) {
    this.userId = params.userId || ''
  }

  async fetchSearchProviderData({
    query = '',
    filters = {},
    predefinedFilters = {},
    sort = '',
    page = 1,
    resultsPerPage = 100,
    destinationZone = 'US',
    selectedCategory,
    isServerSide,
    isPrintsPage,
    userSizePreference,
    shouldAddGroupIdFilter,
    defaultSortObject,
  }: FetchSearchProviderDataParams): FetchSearchProviderDataResponse {
    try {
      // remove query, hitsPerPage and page from the filters sent
      const filtersWithoutBasicQueryParams = rulesToSendFilters({
        filters,
        destinationZone,
        isPrintsPage,
        userSizePreference,
        shouldAddGroupIdFilter,
      })
      const finalFiltersToSearchProvider = {
        ...predefinedFilters,
        ...filtersWithoutBasicQueryParams,
      }
      const { sortBy, sortOrder } = getSortValues({ sort, destinationZone })

      const response = await constructorGetData({
        query,
        filters: finalFiltersToSearchProvider,
        sortBy: Boolean(sortBy) ? sortBy : defaultSortObject?.sortBy,
        sortOrder: Boolean(sortOrder) ? sortOrder : defaultSortObject?.sortOrder,
        page,
        resultsPerPage,
        selectedCategory: isPrintsPage ? 'prints' : selectedCategory,
        isServerSide,
        userId: this.userId,
      })

      if (response.type === 'redirect') {
        return {
          success: true,
          payload: {
            data: {
              type: 'redirect',
              redirectLink: response.redirect?.data?.url || '',
              matechedTerms: response.redirect?.matched_terms || [],
            },
          },
        }
      }
      const transformedResults = transformConstructorSearchResults({
        searchRequest: response.request,
        searchResults: response.results,
        destinationZone,
      })
      const transformedFacets = transformConstructorFacetsToFilters(response.facets)

      return {
        success: true,
        payload: {
          data: {
            type: 'products',
            results: transformedResults,
            facets: transformedFacets,
            totalResults: response.totalResults,
          },
        },
      }
    } catch (error) {
      console.error('Error fetching search results from Constructor.io:', error)
      return {
        success: false,
        payload: { message: 'Error fetching search results from Constructor.io' },
      }
    }
  }

  async fetchSearchProviderFacets({
    query = '',
    filters = {},
    predefinedFilters = {},
    sort = '',
    page = 1,
    resultsPerPage = 100,
    destinationZone = 'US',
    selectedCategory,
    isServerSide,
    isPrintsPage,
    userSizePreference,
    shouldAddGroupIdFilter,
    defaultSortObject,
  }: FetchSearchProviderFacetsParams): FetchSearchProviderFacetsResponse {
    try {
      // remove query, hitsPerPage and page from the filters sent
      const filtersWithoutBasicQueryParams = rulesToSendFilters({
        filters,
        destinationZone,
        isPrintsPage,
        userSizePreference,
        shouldAddGroupIdFilter,
      })
      const finalFiltersToSearchProvider = {
        ...predefinedFilters,
        ...filtersWithoutBasicQueryParams,
      }
      const { sortBy, sortOrder } = getSortValues({ sort, destinationZone })

      const facetData = await constructorGetFacets({
        query,
        filters: finalFiltersToSearchProvider,
        sortBy: Boolean(sortBy) ? sortBy : defaultSortObject?.sortBy,
        sortOrder: Boolean(sortOrder) ? sortOrder : defaultSortObject?.sortOrder,
        page,
        resultsPerPage,
        selectedCategory: isPrintsPage ? 'prints' : selectedCategory,
        isServerSide,
        userId: this.userId,
      })
      const facetFromConstructor = Array.isArray(facetData) ? facetData : []
      const transformedFacets = transformConstructorFacetsToFilters(facetFromConstructor)

      return { success: true, payload: { data: transformedFacets } }
    } catch (error) {
      console.error('Error fetching facets from Constructor.io:', error)
      return {
        success: false,
        payload: { message: 'Error fetching facets from Constructor.io' },
      }
    }
  }

  async fetchSearchProviderAutocomplete({
    query = '',
    filters = {},
    numResults,
    shouldLowerCaseText,
    isServerSide,
  }: FetchSearchProviderAutocompleteParams): FetchSearchProviderAutocompleteResponse {
    try {
      const onlySpaces = query?.trim()?.length === 0
      if (onlySpaces) {
        return {
          success: true,
          payload: {
            data: {
              matchedTerm: '',
              autocompleteSuggestions: [],
              totalResults: 0,
            },
          },
        }
      }

      const { request, sections, totalResultsPerSection } = await constructorGetAutocomplete({
        query,
        numResults,
        filters,
        isServerSide,
        userId: this.userId,
      })

      const autocompleteResults = transformConstructorAutocompleteResults(
        sections,
        shouldLowerCaseText
      )

      return {
        success: true,
        payload: {
          data: {
            matchedTerm: request?.term || '',
            autocompleteSuggestions: autocompleteResults,
            totalResults: totalResultsPerSection.Products,
          },
        },
      }
    } catch (error) {
      console.error('Error fetching autocomplete results from Constructor.io:', error)
      return {
        success: false,
        payload: { message: 'Error fetching autocomplete results from Constructor.io' },
      }
    }
  }

  async fetchSearchProviderRecommendations<T>({
    podId = '',
    filters = {},
    numResults,
    section,
    isServerSide,
  }: FetchSearchProviderRecommendationsParams): FetchSearchProviderRecommendationsResponse<T> {
    try {
      const { response, totalResults, resultId } = await constructorGetRecommendations<T>({
        podId,
        filters,
        numResults,
        section,
        isServerSide,
        userId: this.userId,
      })

      return {
        success: true,
        payload: {
          data: {
            recommendations: response?.results || [],
            totalResults,
            resultId,
          },
        },
      }
    } catch (error) {
      console.error('Error fetching recommendations from Constructor.io:', error)
      return {
        success: false,
        payload: { message: 'Error fetching recommendations from Constructor.io' },
      }
    }
  }
}
